import Phaser from 'phaser';
import { useStore } from '@/infra/store';
import { eventBus } from '@/infra/event-bus/EventBus';
import { logger } from '@/infra/logger/Logger';
import {
  PLAYER_SPEED,
  WORLD_PIXEL_W,
  WORLD_PIXEL_H,
  TILE_SIZE,
  type BiomeId,
} from '@/domain/world/types';
import { regionNpcSpots } from '@/domain/world/region';
import { dayPhaseFromMs } from '@/domain/world/dayphase';
import { getInputAdapter } from '@/services/input';
import { buildWorldRenderer, type WorldRendererHandle } from '../render/WorldRenderer';
import { buildAmbient, type AmbientHandle } from '../render/AmbientRenderer';
import { buildTreeRenderer, type TreeRendererHandle } from '../render/TreeRenderer';
import { buildMemorialRenderer, type MemorialRendererHandle } from '../render/MemorialRenderer';
import {
  buildDirectionalCreatureRenderer,
  type DirectionalCreatureRendererHandle,
} from '../render/DirectionalCreatureRenderer';
import { getWorldService } from '@/services/world';
import { regionRegistry } from '@/domain/world/region-registry';
import { placeNpcInRegion } from '@/domain/world/npc-placement';
import { moveActorInActiveRegion } from './region-movement-adapter';
import { activeRegionalDebugOverlays, activeWorldVisualOverride } from '@/dev/test-lab';
import { boundsOfShape } from '@/domain/world/geometry';
import type { ActorFootprint } from '@/domain/world/geometry';
import { footprintForPresentation } from '@/domain/world/actor-presentation';
import { displayHeightForStage } from '@/domain/world/world-scale';
import { cameraViewForWorldPoint, worldYSortDepth } from './world-visual-adapter';
import { biomeAtWorldPoint } from '@/domain/world/continuous-world';
import { CURRENT_CONTINUOUS_WORLD } from '@/domain/world/regions/current-world';

export class WorldScene extends Phaser.Scene {
  static readonly KEY = 'WorldScene';

  private worldRenderer?: WorldRendererHandle;
  private ambient?: AmbientHandle;
  private creatureRenderer?: DirectionalCreatureRendererHandle;
  private itemSprites = new Map<string, Phaser.GameObjects.Container>();
  private npcSprites = new Map<string, Phaser.GameObjects.Container>();
  private treeSprites = new Map<string, TreeRendererHandle>();
  private memorialSprites = new Map<string, MemorialRendererHandle>();
  private nightOverlay?: Phaser.GameObjects.Rectangle;
  private debugGeometry?: Phaser.GameObjects.Graphics;
  private storeUnsub?: () => void;
  private busUnsubs: Array<() => void> = [];
  private playerRadius = 18;
  private playerFootprint: ActorFootprint = { radiusX: 18, radiusY: 18 };
  private currentBiomeId: BiomeId = 'meadow';
  private alive = true;

  constructor() {
    super({ key: WorldScene.KEY });
  }

  create(): void {
    this.alive = true;
    const c = useStore.getState().creature;
    if (!c) {
      logger.warn('world-scene.no-creature');
      return;
    }

    const reducedMotion = useStore.getState().settings.accessibility.reducedMotion;

    // Renderer do mundo (estático)
    this.worldRenderer = buildWorldRenderer(this);
    // Particles ambientais (vida)
    this.ambient = buildAmbient(this);

    // NPCs estáticos
    this.spawnNpcs();

    // Items
    this.spawnItems();

    // U6: Trees + Memorials
    this.spawnTreesAndMemorials();

    // Criatura controlável (vira o "player")
    this.creatureRenderer = buildDirectionalCreatureRenderer(this, c, { reducedMotion });
    this.playerFootprint = footprintForPresentation(c.genome.body, displayHeightForStage(c.lifecycle.stage));
    const start = useStore.getState();
    this.creatureRenderer.container.setPosition(start.playerX, start.playerY);
    this.updateActorDepth();

    // Câmera
    this.applyRegionCamera(start.playerX, start.playerY);
    this.cameras.main.startFollow(this.creatureRenderer.container, true, 0.1, 0.1);
    this.cameras.main.setDeadzone(60, 60);

    // Day/night overlay
    this.nightOverlay = this.add
      .rectangle(0, 0, WORLD_PIXEL_W, WORLD_PIXEL_H, 0x10204a, 0)
      .setOrigin(0)
      .setDepth(10_000)
      .setScrollFactor(1);
    this.updateDayNight();
    if (import.meta.env.DEV) this.debugGeometry = this.add.graphics().setDepth(20_000);

    // Subscribers
    this.storeUnsub = useStore.subscribe((s) => {
      if (!this.alive) return;
      if (s.creature && this.creatureRenderer) {
        this.creatureRenderer.updateCreature(s.creature);
        this.playerFootprint = footprintForPresentation(
          s.creature.genome.body,
          displayHeightForStage(s.creature.lifecycle.stage),
        );
      }
      if (s.inExpedition) {
        this.alive = false;
        this.scene.stop(WorldScene.KEY);
        this.scene.start('ExpeditionScene');
        return;
      }
      if (!s.inWorld) {
        // Voltar pra house
        this.alive = false;
        this.scene.stop(WorldScene.KEY);
        this.scene.start('HouseScene');
      }
    });
    this.busUnsubs.push(eventBus.on('world.item-collected', () => this.refreshItems()));

    // Input
    getInputAdapter().install();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.alive = false;
      this.storeUnsub?.();
      this.busUnsubs.forEach((u) => u());
      this.busUnsubs = [];
      this.worldRenderer?.destroy();
      this.ambient?.destroy();
      this.creatureRenderer?.destroy();
      this.itemSprites.forEach((s) => s.destroy());
      this.npcSprites.forEach((s) => s.destroy());
      this.treeSprites.forEach((s) => s.destroy());
      this.memorialSprites.forEach((s) => s.destroy());
      this.itemSprites.clear();
      this.npcSprites.clear();
      this.treeSprites.clear();
      this.memorialSprites.clear();
      this.nightOverlay?.destroy();
      this.debugGeometry?.destroy();
    });
  }

  override update(_time: number, delta: number): void {
    if (!this.alive || !this.creatureRenderer) return;
    const input = getInputAdapter();
    const v = input.getMovementVector();
    let resolvedX = 0;
    let resolvedY = 0;
    if (v.x === 0 && v.y === 0) {
      // idle
    } else {
      const fromX = this.creatureRenderer.container.x;
      const fromY = this.creatureRenderer.container.y;
      const resolution = moveActorInActiveRegion({
        registry: regionRegistry,
        from: {
          x: this.creatureRenderer.container.x,
          y: this.creatureRenderer.container.y,
        },
        velocity: v,
        speed: PLAYER_SPEED,
        dtSec: delta / 1_000,
        radius: this.playerRadius,
        footprint: this.playerFootprint,
      });
      this.creatureRenderer.container.setPosition(resolution.position.x, resolution.position.y);
      resolvedX = resolution.position.x - fromX;
      resolvedY = resolution.position.y - fromY;
    }
    const visualOverride = import.meta.env.DEV ? activeWorldVisualOverride() : null;
    if (visualOverride?.facing && visualOverride.action) {
      this.creatureRenderer.setDebugAction(visualOverride.facing, visualOverride.action, delta);
    } else if (visualOverride?.facing && visualOverride.locomotion) {
      this.creatureRenderer.setDebugPresentation(visualOverride.facing, visualOverride.locomotion, delta);
    } else {
      this.creatureRenderer.setMovement(resolvedX, resolvedY, delta);
    }
    this.updateActorDepth();

    // Update store
    useStore
      .getState()
      .setPlayerPos(this.creatureRenderer.container.x, this.creatureRenderer.container.y);

    // Pickup
    getWorldService().pickItemNear(
      this.creatureRenderer.container.x,
      this.creatureRenderer.container.y,
      24,
    );

    // Discover region on entry
    const newBiome = biomeAtWorldPoint(CURRENT_CONTINUOUS_WORLD, {
      x: this.creatureRenderer.container.x,
      y: this.creatureRenderer.container.y,
    }, this.currentBiomeId);
    if (newBiome !== this.currentBiomeId) {
      const from = this.currentBiomeId;
      this.currentBiomeId = newBiome;
      this.applyRegionCamera(
        this.creatureRenderer.container.x,
        this.creatureRenderer.container.y,
      );
      useStore.getState().setLastBiome(newBiome);
      useStore.getState().discoverRegion(newBiome);
      eventBus.emit('world.biome-changed', { from, to: newBiome });
      eventBus.emit('world.region-discovered', { biome: newBiome });
    }

    // Day/night refresh a cada ~30 frames
    if (Phaser.Math.Between(0, 60) === 0) this.updateDayNight();
    this.updateDebugGeometry();
  }

  private applyRegionCamera(x: number, y: number): void {
    const view = cameraViewForWorldPoint(regionRegistry, { x, y });
    this.cameras.main.setBounds(view.bounds.x, view.bounds.y, view.bounds.w, view.bounds.h);
    this.cameras.main.setZoom(view.zoom);
    this.currentBiomeId = biomeAtWorldPoint(CURRENT_CONTINUOUS_WORLD, { x, y }, this.currentBiomeId);
  }

  private updateActorDepth(): void {
    if (!this.creatureRenderer) return;
    const depth = worldYSortDepth(this.creatureRenderer.container.y);
    this.creatureRenderer.setWorldDepth(depth.object);
  }

  private updateDebugGeometry(): void {
    const graphics = this.debugGeometry;
    if (!graphics || !this.creatureRenderer) return;
    graphics.clear();
    const enabled = activeRegionalDebugOverlays();
    if (enabled.size === 0) return;
    const region = regionRegistry.regionAtWorldPoint({
      x: this.creatureRenderer.container.x,
      y: this.creatureRenderer.container.y,
    });
    if (enabled.has('collision')) {
      graphics.lineStyle(2, 0xff4050, 0.9);
      for (const shape of region.collision) {
        if (shape.kind === 'rect') {
          graphics.strokeRect(shape.x, shape.y, shape.w, shape.h);
          continue;
        }
        const first = shape.points[0];
        if (!first) continue;
        graphics.beginPath();
        graphics.moveTo(first.x, first.y);
        for (const point of shape.points.slice(1)) graphics.lineTo(point.x, point.y);
        graphics.closePath();
        graphics.strokePath();
      }
      graphics.lineStyle(2, 0x55dfff, 0.95);
      graphics.strokeEllipse(
        this.creatureRenderer.container.x,
        this.creatureRenderer.container.y,
        this.playerFootprint.radiusX * 2,
        this.playerFootprint.radiusY * 2,
      );
    }
    if (enabled.has('zones')) {
      graphics.lineStyle(2, 0x45f0b0, 0.8);
      for (const zone of region.zones) {
        const bounds = boundsOfShape(zone.area);
        graphics.strokeRect(bounds.x, bounds.y, bounds.w, bounds.h);
      }
    }
    if (enabled.has('entrances')) {
      graphics.lineStyle(3, 0xffd060, 0.9);
      for (const entrance of region.entrances) {
        graphics.strokeRect(entrance.area.x, entrance.area.y, entrance.area.w, entrance.area.h);
      }
    }
    if (enabled.has('spawns')) {
      graphics.fillStyle(0x80e8ff, 0.9);
      for (const zone of region.zones.filter((candidate) => ['resource', 'npc', 'fauna', 'wild', 'dominant'].includes(candidate.kind))) {
        const bounds = boundsOfShape(zone.area);
        graphics.fillCircle(bounds.x + bounds.w / 2, bounds.y + bounds.h / 2, 5);
      }
    }
    if (enabled.has('camera')) {
      const bounds = region.camera.bounds;
      graphics.lineStyle(3, 0xc080ff, 0.85);
      graphics.strokeRect(bounds.x, bounds.y, bounds.w, bounds.h);
    }
  }

  private updateDayNight(): void {
    if (!this.nightOverlay) return;
    const phase = dayPhaseFromMs(Date.now());
    this.nightOverlay.setFillStyle(0x10204a, phase === 'night' ? 0.42 : 0);
  }

  private spawnTreesAndMemorials(): void {
    const w = useStore.getState().world;
    const trees = w.trees ?? [];
    for (const t of trees) {
      const r = buildTreeRenderer(this, t);
      r.container.setDepth(worldYSortDepth(t.y).object);
      this.treeSprites.set(t.id, r);
    }
    const memorials = w.memorials ?? [];
    for (const m of memorials) {
      const r = buildMemorialRenderer(this, m);
      r.container.setDepth(worldYSortDepth(m.y).object);
      this.memorialSprites.set(m.id, r);
    }
  }

  private spawnNpcs(): void {
    const npcs = useStore.getState().npcs;
    const occupiedByBiome = new Map<string, Array<{ x: number; y: number }>>();
    for (const n of npcs) {
      if (n.dead || !n.homeBiome) continue;
      const occupied = occupiedByBiome.get(n.homeBiome) ?? [];
      const region = regionRegistry.get(n.homeBiome);
      const placement = region ? placeNpcInRegion(region, n.id, occupied) : null;
      let pos: { x: number; y: number };
      if (placement?.ok) {
        pos = placement.point;
      } else {
        const fallbackSpots = regionNpcSpots(n.homeBiome);
        pos = fallbackSpots[occupied.length % fallbackSpots.length]!;
      }
      occupied.push(pos);
      occupiedByBiome.set(n.homeBiome, occupied);
      const sprite = this.add.container(pos.x, pos.y);
      const g = this.add.graphics();
      g.fillStyle(0x999999, 1);
      g.fillCircle(0, 0, 12);
      g.lineStyle(1, 0x444444, 0.8);
      g.strokeCircle(0, 0, 12);
      const label = this.add
        .text(0, 18, n.name, {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#ddd',
        })
        .setOrigin(0.5);
      sprite.add([g, label]);
      sprite.setDepth(worldYSortDepth(pos.y).object);
      this.npcSprites.set(n.id, sprite);
    }
  }

  private spawnItems(): void {
    const w = useStore.getState().world;
    for (const biome of Object.keys(w.regionItems) as Array<keyof typeof w.regionItems>) {
      for (const it of w.regionItems[biome]) {
        if (it.collected) continue;
        const sprite = this.makeItemSprite(it.type);
        sprite.setPosition(it.x, it.y);
        sprite.setDepth(worldYSortDepth(it.y).object);
        // Bobbing TWEEN relativo à posição final
        this.tweens.add({
          targets: sprite,
          y: it.y - 4,
          duration: 700,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.InOut',
        });
        this.itemSprites.set(it.id, sprite);
      }
    }
  }

  private refreshItems(): void {
    const w = useStore.getState().world;
    const allCollected = new Set<string>();
    for (const biome of Object.keys(w.regionItems) as Array<keyof typeof w.regionItems>) {
      for (const it of w.regionItems[biome]) {
        if (it.collected) allCollected.add(it.id);
      }
    }
    for (const id of Array.from(this.itemSprites.keys())) {
      if (allCollected.has(id)) {
        this.itemSprites.get(id)?.destroy();
        this.itemSprites.delete(id);
      }
    }
  }

  private makeItemSprite(
    type: 'berry' | 'shell' | 'crystal' | 'flower',
  ): Phaser.GameObjects.Container {
    const c = this.add.container(0, 0);
    const g = this.add.graphics();
    switch (type) {
      case 'berry':
        g.fillStyle(0xff4f6a, 1);
        g.fillCircle(0, 0, 6);
        g.fillStyle(0x4a7e2a, 1);
        g.fillRect(-1, -8, 2, 4);
        g.fillStyle(0xffffff, 0.7);
        g.fillCircle(-2, -2, 1);
        break;
      case 'shell':
        g.fillStyle(0xffe0c8, 1);
        g.fillEllipse(0, 0, 14, 10);
        g.lineStyle(1, 0x8a5a3a, 1);
        g.lineBetween(-5, 0, 5, 0);
        g.lineBetween(-3, -3, 3, 3);
        break;
      case 'crystal':
        g.fillStyle(0x4ad4ff, 1);
        g.fillTriangle(0, -8, -6, 4, 6, 4);
        g.fillStyle(0xb0eaff, 1);
        g.fillTriangle(0, -6, -2, 0, 2, 0);
        break;
      case 'flower':
        g.fillStyle(0xff8db1, 1);
        g.fillCircle(-3, 0, 3);
        g.fillCircle(3, 0, 3);
        g.fillCircle(0, -3, 3);
        g.fillCircle(0, 3, 3);
        g.fillStyle(0xffe066, 1);
        g.fillCircle(0, 0, 2);
        break;
    }
    c.add(g);
    return c;
  }

  // helpers para tile coords (não usado ainda, deixei só para futuras extensões)
  private static tilePixel(t: number): number {
    return t * TILE_SIZE;
  }
  // suppress unused
  static _tilePixel = WorldScene.tilePixel;
}
